import fetch from 'isomorphic-fetch';

const STOREFRONT_API_URL = `https://${process.env.GATSBY_SHOPIFY_DOMAIN}/api/2024-01/graphql.json`;

const HEADERS = {
    'Content-Type': 'application/json',
    'X-Shopify-Storefront-Access-Token': process.env.GATSBY_SHOPIFY_STOREFRONT_API_TOKEN,
};

// GraphQL Queries
const productFields = `
    id
    title
    handle
    vendor
    productType
    tags
    availableForSale
    variants(first: 100) {
        edges {
            node {
                id
                title
                sku
                availableForSale
                price {
                    amount
                    currencyCode
                }
                compareAtPrice {
                    amount
                    currencyCode
                }
                image {
                    url
                    altText
                }
                selectedOptions {
                    name
                    value
                }
            }
        }
    }
    images(first: 100) {
        edges {
            node {
                url
                altText
            }
        }
    }
`;

const productByHandleQuery = `
    query getProductByHandle($handle: String!) {
        productByHandle(handle: $handle) {
            ${productFields}
        }
    }
`;

const productByIdQuery = `
    query getProductById($id: ID!) {
        product(id: $id) {
            ${productFields}
        }
    }
`;

const cartFields = `{
    id
    checkoutUrl
    note
    buyerIdentity {
        countryCode
    }
    createdAt
    updatedAt
    totalQuantity
    cost {
        subtotalAmount {
            amount
            currencyCode
        }
        totalAmount {
            amount
            currencyCode
        }
    }
    lines(first: 100) {
        edges {
            node {
                id
                quantity
                merchandise {
                    ... on ProductVariant {
                        id
                        title
                        sku
                        image {
                            url
                            altText
                        }
                        price {
                            amount
                            currencyCode
                        }
                        product {
                            id
                            title
                            handle
                            vendor
                        }
                        selectedOptions {
                            name
                            value
                        }
                    }
                }
            }
        }
    }
}`;

const cartCreateMutation = `
    mutation cartCreate($buyerIdentity: CartBuyerIdentityInput) {
        cartCreate(input: { buyerIdentity: $buyerIdentity }) {
            cart ${cartFields}
            userErrors {
                field
                message
            }
        }
    }
`;

const cartQuery = `
    query getCart($id: ID!) {
        cart(id: $id) ${cartFields}
    }
`;

const cartLinesAddMutation = `
    mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
        cartLinesAdd(cartId: $cartId, lines: $lines) {
            cart ${cartFields}
            userErrors {
                field
                message
            }
        }
    }
`;

const cartLinesUpdateMutation = `
    mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
        cartLinesUpdate(cartId: $cartId, lines: $lines) {
            cart ${cartFields}
            userErrors {
                field
                message
            }
        }
    }
`;

const cartLinesRemoveMutation = `
    mutation cartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
        cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
            cart ${cartFields}
            userErrors {
                field
                message
            }
        }
    }
`;

// Helper function to make GraphQL requests
const makeGraphQLRequest = async (query, variables = {}) => {
    try {
        const response = await fetch(STOREFRONT_API_URL, {
            method: 'POST',
            headers: HEADERS,
            body: JSON.stringify({
                query,
                variables,
            }),
        });

        const data = await response.json();

        if (data.errors) {
            console.error('GraphQL errors:', data.errors);
            return null;
        }

        return data.data;
    } catch (error) {
        console.error('Request error:', error);
        return null;
    }
};

// Utility to flatten edges/nodes structure
const flattenConnection = (connection) => {
    if (!connection || !connection.edges) return [];
    return connection.edges.map(edge => edge.node);
};

// Cart operations
export const createCart = async () => {
    const data = await makeGraphQLRequest(cartCreateMutation, {
        buyerIdentity: {
            countryCode: 'AU'
        }
    });

    if (data?.cartCreate?.cart) {
        const cart = data.cartCreate.cart;
        // Flatten the lines
        cart.lineItems = flattenConnection(cart.lines);
        return cart;
    }

    return null;
};

export const fetchCart = async (cartId) => {
    const data = await makeGraphQLRequest(cartQuery, { id: cartId });

    if (data?.cart) {
        const cart = data.cart;
        // Flatten the lines
        cart.lineItems = flattenConnection(cart.lines);
        return cart;
    }

    return null;
};

export const addToCart = async (cartId, variantId, quantity = 1) => {
    const lines = [{
        merchandiseId: variantId,
        quantity: quantity
    }];

    const data = await makeGraphQLRequest(cartLinesAddMutation, {
        cartId,
        lines
    });

    if (data?.cartLinesAdd?.cart) {
        const cart = data.cartLinesAdd.cart;
        // Flatten the lines
        cart.lineItems = flattenConnection(cart.lines);
        return cart;
    }

    return null;
};

export const updateCartLine = async (cartId, lineId, quantity) => {
    const lines = [{
        id: lineId,
        quantity: quantity
    }];

    const data = await makeGraphQLRequest(cartLinesUpdateMutation, {
        cartId,
        lines
    });

    if (data?.cartLinesUpdate?.cart) {
        const cart = data.cartLinesUpdate.cart;
        // Flatten the lines
        cart.lineItems = flattenConnection(cart.lines);
        return cart;
    }

    return null;
};

export const removeCartLine = async (cartId, lineId) => {
    const data = await makeGraphQLRequest(cartLinesRemoveMutation, {
        cartId,
        lineIds: [lineId]
    });

    if (data?.cartLinesRemove?.cart) {
        const cart = data.cartLinesRemove.cart;
        // Flatten the lines
        cart.lineItems = flattenConnection(cart.lines);
        return cart;
    }

    return null;
};

// Product operations
export const fetchProductByHandle = async (handle) => {
    const data = await makeGraphQLRequest(productByHandleQuery, { handle });

    if (data?.productByHandle) {
        const product = data.productByHandle;
        // Flatten variants to match Buy SDK structure
        product.variants = flattenConnection(product.variants);
        product.images = flattenConnection(product.images);
        return product;
    }

    return null;
};

export const fetchProductById = async (productId) => {
    const data = await makeGraphQLRequest(productByIdQuery, { id: productId });

    if (data?.product) {
        const product = data.product;
        // Flatten variants to match Buy SDK structure
        product.variants = flattenConnection(product.variants);
        product.images = flattenConnection(product.images);
        return product;
    }

    return null;
};

export default {
    createCart,
    fetchCart,
    addToCart,
    updateCartLine,
    removeCartLine,
    fetchProductByHandle,
    fetchProductById,
};